import { useAnalytics } from '../context/AnalyticsContext';

const PALETTE = [
	'#2563EB','#059669','#D97706','#7C3AED','#DC2626',
	'#0891B2','#65A30D','#C2410C','#9333EA','#0369A1',
	'#BE185D','#0F766E','#B45309','#4338CA','#B91C1C',
	'#0369A1','#15803D','#C2410C','#6D28D9','#0E7490',
];

export default function TopModulesChart() {
	const { state, selectModule } = useAnalytics();
	const { topModules, filteredModules, selectedModule, selectedUser, selectedRole, loading, filterLoading, filterType } = state;

	// Show filtered data whenever ANY filter is active
	const anyFilter   = !!filterType;
	// Modules chart is clickable unless a USER filter is the drill-down
	// (when user is selected we show which modules they accessed — not clickable)
	const isUserDrill = filterType === 'user';
	const isModuleSel = filterType === 'module' && !!selectedModule;

	const data = anyFilter ? filteredModules : topModules;
	const busy = anyFilter ? filterLoading   : loading;
	const max  = Math.max(...(data?.map(m => m.views) ?? [1]), 1);

	if (busy) return <div style={S.empty}>Loading…</div>;
	if (!data?.length) return (
		<div style={S.empty}>
			{filterType === 'user' ? `No modules accessed by "${selectedUser?.label}"`
			: filterType === 'role' ? `No modules for role "${selectedRole?.label}"`
			: 'No data for the selected period'}
		</div>
	);

	return (
		<div style={S.scrollWrap}>
			{data.map((mod, i) => {
				const pct        = Math.round((mod.views / max) * 100);
				const col        = PALETTE[i % PALETTE.length];
				const isSelected = isModuleSel && selectedModule?.id === mod.id;
				const isOther    = isModuleSel && !isSelected;
				const clickable  = !isUserDrill;

				return (
					<div
						key={mod.id ?? i}
						onClick={() => clickable && selectModule({ id: mod.id, label: mod.label })}
						style={{ ...S.row, opacity: isOther ? 0.35 : 1, cursor: clickable ? 'pointer' : 'default' }}
						title={clickable ? (isSelected ? 'Click to clear filter' : `Filter by ${mod.label}`) : undefined}
					>
						<div style={S.labelRow}>
							<div style={S.nameWrap}>
								<span style={{
									...S.rankDot,
									background: isSelected ? col : col + '20',
									color: isSelected ? '#fff' : col,
									boxShadow: isSelected ? `0 0 0 2px ${col}` : 'none',
								}}>
									{i + 1}
								</span>
								<span style={{ ...S.name, fontWeight: isSelected ? 700 : 400 }} title={mod.label}>
									{mod.label ?? '—'}
								</span>
								{isSelected && <span style={S.tag}>filtered</span>}
							</div>
							<span style={{ ...S.count, color: col }}>{(mod.views ?? 0).toLocaleString('en-GB')}</span>
						</div>
						<div style={S.track}>
							<div style={{ ...S.fill, width: `${pct}%`, background: col }} />
						</div>
					</div>
				);
			})}
		</div>
	);
}

const S = {
	scrollWrap: { overflowY: 'auto', maxHeight: 360, display: 'flex', flexDirection: 'column', gap: 11, paddingRight: 4 },
	row:        { transition: 'opacity .2s', flexShrink: 0 },
	labelRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 },
	nameWrap:   { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 },
	rankDot: {
		width: 22, height: 22, borderRadius: 6, flexShrink: 0,
		display: 'flex', alignItems: 'center', justifyContent: 'center',
		fontSize: 11, fontWeight: 700, transition: 'all .2s',
	},
	name:  { fontSize: 13, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
	tag:   { fontSize: 10, fontWeight: 700, color: '#2563EB', background: '#EFF6FF', padding: '1px 6px', borderRadius: 10, flexShrink: 0 },
	count: { fontSize: 13, fontWeight: 700, flexShrink: 0, marginLeft: 8 },
	track: { height: 6, background: '#F1F5F9', borderRadius: 99, overflow: 'hidden' },
	fill:  { height: '100%', borderRadius: 99, transition: 'width .8s cubic-bezier(.4,0,.2,1)' },
	empty: { color: '#94A3B8', textAlign: 'center', padding: '32px 0', fontSize: 13 },
};